import { ChangeEvent, Fragment, useState } from "react"

import { css, cx } from "@emotion/css"

import Autocomplete from "@material-ui/lab/Autocomplete"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { ErrorsMap } from "api/http/utils"
import { TextInput } from "components/inputs/TextInput"
import { useI18n } from "providers/I18n"

export type Option = {
  label: string | undefined
  value: string | undefined
} | null

type Props<T> = {
  onClose: VoidFunction
  isLoading: boolean
  onChange: (name: string, value: string) => void
  onSelectChange: (option: Option | null) => void
  handleCreate: VoidFunction
  fieldName: string
  data?: T[]
  row?: Partial<T>
  labelParam?: string
  errors?: ErrorsMap
}

export function AddNewTableRow<T extends { label?: string; name?: string }>(
  props: Props<T>,
) {
  const { t } = useI18n()
  const {
    onClose,
    isLoading,
    data,
    row,
    onChange,
    onSelectChange,
    handleCreate,
    fieldName,
    labelParam,
    errors,
  } = props
  const [newRow, setNewRow] = useState<boolean>(false)

  const options =
    data?.map((d) => {
      const option = d?.label || d?.name
      return { label: option, value: option }
    }) ?? []

  const option = options?.find(
    (opt) => opt.value === (row?.label || row?.name),
  ) || {
    value: "",
    label: "",
  }

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value, name } = e.target
    onChange(name, value)
  }

  const handleSelectChange = (_event: ChangeEvent<{}>, option: Option | null) =>
    onSelectChange(option)

  const handleClick = () => {
    setNewRow(true)
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {!newRow ? (
          <>
            <Autocomplete
              placeholder={t(`${labelParam}.add.placeholder`)}
              options={options || []}
              value={option}
              onChange={handleSelectChange}
              className={styles.input}
              getOptionLabel={(option) => option.label as string}
              getOptionSelected={(option, value) =>
                option.label === value.label
              }
              disableClearable={!option?.label}
              popupIcon={<DropdownIcon />}
              renderInput={(prms) => (
                <TextInput
                  {...prms}
                  name={fieldName}
                  size="medium"
                  margin="normal"
                  label={t(`${labelParam}.add.label`)}
                  placeholder={t(`${labelParam}.add.placeholder`)}
                  error={!!errors && !!errors[fieldName]}
                  errorMessage={!!errors ? errors[fieldName] : ""}
                  InputProps={{
                    ...prms.InputProps,
                    className: cx("BodyBody-2BlackRegular", styles.selectInput),
                    endAdornment: (
                      <Fragment>{prms.InputProps.endAdornment}</Fragment>
                    ),
                  }}
                />
              )}
            />
            <span className={cx("BodyBody-2BlackLight", styles.span)}>
              {t(`${labelParam}.or`)}
            </span>
            <Button
              color="primary"
              className={styles.textButton}
              onClick={handleClick}
            >
              {t(`${labelParam}.create_button`)}
            </Button>
          </>
        ) : (
          <TextInput
            name={fieldName}
            size="medium"
            color="primary"
            onChange={handleChange}
            label={t(`${labelParam}.add.label`)}
            className={cx("BodyBody-2BlackRegular", styles.input)}
            error={!!errors && !!errors[fieldName]}
            errorMessage={!!errors ? errors[fieldName] : ""}
          />
        )}
      </div>
      <ButtonGroup className={styles.actions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          disabled={isLoading}
        >
          {t("warning.modal.cancel")}
        </Button>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          {t("button.add")}
        </Button>
      </ButtonGroup>
    </div>
  )
}

const styles = {
  wrapper: css`
    border-radius: 4px;
    box-shadow: 1px 6px 10px 0 var(--disabled-grey);
    border: solid 1px var(--lighter-grey);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-s);
    margin-bottom: var(--spacing-m);
  `,
  content: css`
    display: flex;
    align-items: center;
  `,
  selectInput: css`
    width: 250px;
    padding-right: var(--spacing-xxl-4);
    .MuiFormControl-marginNormal {
      margin: 0;
    }
    .MuiAutocomplete-endAdornment {
      right: 9px;
    }
  `,
  input: css`
    width: 250px;
  `,
  span: css`
    margin-right: var(--spacing-xs);
    margin-left: var(--spacing-m);
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
  `,
  actions: css`
    display: flex;
  `,
}
